import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { TrendingUp, TrendingDown, FileText, Users } from "lucide-react";
import { apiConfig } from "../config/api.config";

const COLORS = {
  danger: "#ef4444",
  warning: "#f59e0b",
  success: "#10b981",
  info: "#3b82f6",
  purple: "#8b5cf6",
};

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pickArray(src, keys) {
  for (const k of keys) {
    if (Array.isArray(src?.[k])) return src[k];
  }
  return [];
}

function normalizeAnalitica(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const k = src.kpis && typeof src.kpis === "object" ? src.kpis : {};

  const totalContratos =
    toNum(k.total_contratos) ||
    toNum(k.total_contracts) ||
    toNum(k.totalContracts) ||
    toNum(k.total) ||
    0;

  const totalAprobados =
    toNum(k.total_aprobados) ||
    toNum(k.aprobados) ||
    toNum(k.totalApproved) ||
    0;

  const totalRechazados =
    toNum(k.total_rechazados) ||
    toNum(k.rechazados) ||
    toNum(k.totalRejected) ||
    0;

  const totalEnProceso =
    toNum(k.total_en_proceso) ||
    toNum(k.en_proceso) ||
    toNum(k.totalInProgress) ||
    0;

  const fasesRaw = pickArray(src, [
    "contratos_por_fase_actual",
    "contratosPorFaseActual",
    "fase_actual",
  ]);

  const contratosPorFaseActual = fasesRaw
    .map((x) => ({
      fase: String(x?.fase ?? x?.name ?? "").trim(),
      cantidad: toNum(x?.cantidad ?? x?.count ?? x?.n),
    }))
    .filter((x) =>
      ["Planeación", "Hacienda", "Jurídica", "Alcalde"].includes(x.fase),
    );

  const rechazosRaw = pickArray(src, [
    "rechazos_por_secretaria",
    "rechazos_por_secretarias",
    "rechazosPorSecretaria",
  ]);

  const rechazosPorSecretaria = rechazosRaw.map((x) => ({
    secretaria: String(x?.secretaria ?? x?.name ?? "").trim(),
    total: toNum(x?.total),
    rechazados: toNum(x?.rechazados),
    tasa_rechazo: toNum(x?.tasa_rechazo ?? x?.tasa ?? x?.rate),
  }));

  let topSecretarias = pickArray(src, [
    "top_secretarias",
    "topSecretarias",
    "top3_secretarias",
  ]);

  if (!topSecretarias.length) {
    topSecretarias = [...rechazosPorSecretaria]
      .filter((s) => s.secretaria)
      .sort((a, b) => toNum(a.tasa_rechazo) - toNum(b.tasa_rechazo))
      .slice(0, 3)
      .map((s) => ({
        secretaria: s.secretaria,
        tasa_aprobacion: Math.max(
          0,
          Math.min(100, Math.round((100 - toNum(s.tasa_rechazo)) * 10) / 10),
        ),
        tasa_rechazo: Math.round(toNum(s.tasa_rechazo) * 10) / 10,
      }));
  }

  return {
    ...src,
    top_secretarias: topSecretarias || [],
    rechazos_por_secretaria: rechazosPorSecretaria || [],
    contratos_por_fase_actual: contratosPorFaseActual || [],
    kpis: {
      ...k,
      total_contratos: totalContratos,
      total_aprobados: totalAprobados,
      total_rechazados: totalRechazados,
      total_en_proceso: totalEnProceso,
    },
  };
}

const Analitica = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filtroFecha, setFiltroFecha] = useState("todo");

  useEffect(() => {
  cargarDatos();
}, []);

useEffect(() => {
  const id = setInterval(() => {
    cargarDatos();
  }, 10 * 60 * 1000); 
  return () => clearInterval(id);
}, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const role = localStorage.getItem("role") || "usuario";
      const cacheBust = `t=${Date.now()}`;

      const url = apiConfig.getUrl(
        "/api/analitica.php",
        `role=${encodeURIComponent(role)}&filtro=${encodeURIComponent(
          filtroFecha,
        )}&${cacheBust}`,
      );

      const response = await fetch(url, { credentials: "include" });
      const result = await response.json();

      if (result?.ok) setData(normalizeAnalitica(result.data));
      else setData(null);
    } catch (error) {
      console.error("Error cargando analítica:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fasesOrdenadas = useMemo(() => {
    const base = [
      { fase: "Planeación", cantidad: 0 },
      { fase: "Hacienda", cantidad: 0 },
      { fase: "Jurídica", cantidad: 0 },
      { fase: "Alcalde", cantidad: 0 },
    ];
    const map = new Map(
      (data?.contratos_por_fase_actual || []).map((x) => [
        String(x?.fase ?? "").trim(),
        toNum(x?.cantidad),
      ]),
    );
    return base.map((x) => ({ ...x, cantidad: map.get(x.fase) ?? 0 }));
  }, [data]);

  const faseRezagada = useMemo(() => {
    if (!Array.isArray(fasesOrdenadas) || !fasesOrdenadas.length)
      return { fase: "N/A", cantidad: 0 };
    const max = fasesOrdenadas.reduce(
      (best, cur) => (toNum(cur.cantidad) > toNum(best.cantidad) ? cur : best),
      fasesOrdenadas[0],
    );
    return { fase: max?.fase || "N/A", cantidad: toNum(max?.cantidad) };
  }, [fasesOrdenadas]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          padding: "2rem",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: "center", padding: "4rem 2rem", color: "#6b7280" }}>
        No se pudieron cargar los datos analíticos
      </div>
    );
  }

  const sumCheck =
    toNum(data.kpis?.total_aprobados) +
    toNum(data.kpis?.total_rechazados) +
    toNum(data.kpis?.total_en_proceso);

  const totalKpi = toNum(data.kpis?.total_contratos);

  return (
    <div
      style={{
        padding: "1.5rem",
        maxWidth: "1400px",
        margin: "0 auto",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "1.5rem",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.7rem",
                fontWeight: "800",
                color: "#111827",
                margin: "0 0 0.5rem 0",
              }}
            >
              📊 Analítica de Contratación
            </h1>
            <p style={{ color: "#6b7280", margin: 0, fontSize: "1rem" }}>
              Resumen operativo en tiempo real
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <select
              value={filtroFecha}
              onChange={(e) => setFiltroFecha(e.target.value)}
              style={{
                padding: "0.6rem 1rem",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              <option value="todo">Todo el tiempo</option>
              <option value="30dias">Últimos 30 días</option>
              <option value="90dias">Últimos 90 días</option>
              <option value="año">Último año</option>
            </select>

            <button
              onClick={() => {
                const role = localStorage.getItem("role") || "usuario";
                const url = apiConfig.getUrl(
                  "/api/export/analitica_contratacion.php",
                  `role=${encodeURIComponent(role)}`,
                );
                window.open(url, "_blank");
              }}
              style={{
                padding: "0.6rem 1rem",
                backgroundColor: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "10px",
                fontSize: "1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 700,
              }}
            >
              <FileText size={18} />
              Descargar Excel
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        {/* Total contratos + desglose */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "1.5rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: "1rem", color: "#6b7280", margin: "0 0 0.5rem 0", fontWeight: 700 }}>
                Total de Necesidades
              </p>
              <p style={{ fontSize: "2.2rem", fontWeight: "900", color: "#3b82f6", margin: 0 }}>
                {totalKpi}
              </p>
            </div>
            <div
              style={{
                backgroundColor: "#dbeafe",
                padding: "0.85rem",
                borderRadius: "10px",
                height: "fit-content",
                color: "#3b82f6",
              }}
            >
              <Users size={26} />
            </div>
          </div>

          <p style={{ fontSize: "0.95rem", color: "#9ca3af", margin: "0 0 0.9rem 0" }}>
            Contratos registrados
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.65rem", fontSize: "0.95rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Aprobados:</span>
              <span style={{ fontWeight: 900, color: COLORS.success }}>{toNum(data.kpis?.total_aprobados)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Rechazados:</span>
              <span style={{ fontWeight: 900, color: COLORS.danger }}>{toNum(data.kpis?.total_rechazados)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>En proceso:</span>
              <span style={{ fontWeight: 900, color: COLORS.warning }}>{toNum(data.kpis?.total_en_proceso)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Suma:</span>
              <span
                style={{
                  fontWeight: 900,
                  color: sumCheck === totalKpi ? COLORS.success : COLORS.danger,
                }}
              >
                {sumCheck}
              </span>
            </div>
          </div>

          {sumCheck !== totalKpi && (
            <div style={{ marginTop: "0.7rem", fontSize: "0.95rem", color: COLORS.danger, fontWeight: 700 }}>
              La suma no cuadra con el total. Revisa estados en BD.
            </div>
          )}
        </div>

        {/* Top 3 Secretarías */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1rem" }}>
            <div style={{ backgroundColor: "#d1fae5", padding: "0.6rem", borderRadius: "10px", color: "#10b981" }}>
              <TrendingUp size={22} />
            </div>
            <p style={{ fontSize: "1rem", color: "#6b7280", margin: 0, fontWeight: "800" }}>Top 3 Secretarías</p>
          </div>

          {Array.isArray(data.top_secretarias) && data.top_secretarias.length > 0 ? (
            data.top_secretarias.map((sec, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: idx < 2 ? "0.95rem" : 0,
                  paddingBottom: idx < 2 ? "0.95rem" : 0,
                  borderBottom: idx < 2 ? "1px solid #e5e7eb" : "none",
                }}
              >
                <p style={{ fontSize: "0.95rem", color: "#111827", margin: "0 0 0.35rem 0", fontWeight: "800" }}>
                  {idx + 1}. {sec.secretaria}
                </p>
                <div style={{ display: "flex", gap: "1rem", fontSize: "0.95rem", fontWeight: 800 }}>
                  <span style={{ color: "#10b981" }}>✓ {toNum(sec.tasa_aprobacion)}%</span>
                  <span style={{ color: "#ef4444" }}>✗ {toNum(sec.tasa_rechazo)}%</span>
                </div>
              </div>
            ))
          ) : (
            <p style={{ fontSize: "1rem", color: "#9ca3af", textAlign: "center", padding: "1rem", fontWeight: 700 }}>
              No hay datos disponibles
            </p>
          )}
        </div>

        {/* ✅ SOLO Fase Rezagada (sin promedio) */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.6rem" }}>
            <div style={{ backgroundColor: "#e9d5ff", padding: "0.6rem", borderRadius: "10px", color: "#8b5cf6" }}>
              <TrendingDown size={22} />
            </div>
            <p style={{ fontSize: "1rem", color: "#6b7280", margin: 0, fontWeight: "800" }}>Fase Rezagada</p>
          </div>

          <p style={{ fontSize: "2.1rem", fontWeight: "900", color: "#8b5cf6", margin: "0.6rem 0" }}>
            {faseRezagada.fase}
          </p>
          <p style={{ fontSize: "1rem", color: "#9ca3af", margin: 0, fontWeight: 700 }}>
            {faseRezagada.cantidad} contratos pendientes
          </p>
        </div>
      </div>

      {/* Paneles */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {/* Tasa de Rechazo (TOP 5) */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            padding: "1.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1rem" }}>
            <div style={{ backgroundColor: "#fee2e2", padding: "0.6rem", borderRadius: "10px", color: COLORS.danger }}>
              <TrendingDown size={22} />
            </div>
            <p style={{ fontSize: "1rem", color: "#6b7280", margin: 0, fontWeight: "800" }}>
              Tasa de Rechazo (Top 5)
            </p>
          </div>

          {Array.isArray(data.rechazos_por_secretaria) && data.rechazos_por_secretaria.length > 0 ? (
            [...data.rechazos_por_secretaria]
              .filter((x) => x?.secretaria)
              .sort((a, b) => toNum(b.tasa_rechazo) - toNum(a.tasa_rechazo))
              .slice(0, 5)
              .map((sec, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: idx < 4 ? "1rem" : 0,
                    paddingBottom: idx < 4 ? "1rem" : 0,
                    borderBottom: idx < 4 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <p style={{ fontSize: "0.95rem", color: "#111827", margin: "0 0 0.35rem 0", fontWeight: "800" }}>
                    {idx + 1}. {sec.secretaria}
                  </p>
                  <div style={{ display: "flex", gap: "1rem", fontSize: "0.95rem", fontWeight: 800 }}>
                    <span style={{ color: COLORS.danger }}>✗ {Math.round(toNum(sec.tasa_rechazo) * 10) / 10}%</span>
                    <span style={{ color: "#6b7280" }}>
                      ({toNum(sec.rechazados)} / {toNum(sec.total)})
                    </span>
                  </div>
                </div>
              ))
          ) : (
            <p style={{ fontSize: "1rem", color: "#9ca3af", textAlign: "center", padding: "1rem", fontWeight: 700 }}>
              No hay datos disponibles
            </p>
          )}
        </div>

        {/* Contratos por fase */}
        <ChartCard title="🔍 Contratos por Fase Actual" subtitle="Pendientes por fase">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={fasesOrdenadas} margin={{ top: 34, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fase" fontSize={14} />
              <YAxis allowDecimals={false} fontSize={14} domain={[0, (dataMax) => Math.max(1, dataMax + 3)]} />
              <Tooltip />
              <Bar dataKey="cantidad" fill={COLORS.info} name="Contratos">
                <LabelList dataKey="cantidad" position="top" offset={10} fontSize={16} fontWeight="bold" />
                {fasesOrdenadas.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS.info} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children }) => (
  <div
    style={{
      backgroundColor: "white",
      borderRadius: "10px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      padding: "1.5rem",
    }}
  >
    <h3 style={{ fontSize: "1.2rem", fontWeight: "900", marginBottom: "0.5rem" }}>{title}</h3>
    <p style={{ fontSize: "1rem", color: "#6b7280", marginBottom: "1rem", fontWeight: 700 }}>{subtitle}</p>
    {children}
  </div>
);

export default Analitica;
